import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "../../../../containers/app/layout";
import { EmployeesWrapper, FiltersCard, EmployeeTableCard } from "./styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { Label, P } from "../../../../components/typography/styled";
import { getAllEmployees } from "../../../../utils/apis/employee/getAllEmployees";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import { BaseInput } from "../../../../components/form/input/styled";
import { getDepartments } from "../../../../utils/apis/department/getDepartments";
import { deleteEmployeeService } from "../../../../utils/apis/employee/deleteEmployee";
import { SuccessModal } from "../../../../containers/app/modals/successmodal";
import { AddEmployeeModal } from "../../../../containers/app/modals/addemployeemodal";
import { Context } from "../../../../context";
import { EmployeeBulkUploadModal } from "../../../../containers/app/modals/employeebulkuploadmodal";
import { getCompanyDetails } from "../../../../utils/apis/company/getCompanyDetails";
import { getPlanService } from "../../../../utils/apis/plansandpricing/getPlan";
import { EditPlanModal } from "../../../../containers/app/modals/editplanmodal";
import { getBulkEmployeeUploadTemplate } from "../../../../utils/apis/employee/bulkUploadTemplate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faFilter } from "@fortawesome/free-solid-svg-icons";

export const Employees = () => {
  const PAGE_SIZE = 10;

  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");
  const COMPANY_ID = cookies.get("COMPANY_ID");

  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const {
    setIsAddEmployeeModalOpen,
    isEmployeeBulkUploadModalOpen,
    setIsEmployeeBulkUploadModalOpen,
  } = useContext(Context);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activeEmployeeId, setActiveEmployeeId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState({
    employeeName: "",
    departmentId: "",
    jobTitle: "",
  });
  const [flag, setFlag] = useState(null);
  const [payrollPlan, setPayrollPlan] = useState(null);
  const [bulkImportAnchorElement, setBulkImportAnchorElement] = useState(null);

  const handleCloseSuccessModal = () => {
    setFlag(null);
    return setIsSuccessModalOpen(false);
  };

  const handlePersistModal = () => {
    setIsSuccessModalOpen(true);
  };

  useEffect(() => {
    getAllEmployees(TOKEN, COMPANY_ID, filter)
      .then((data) => setEmployees(data))
      .catch((err) => {
        console.error("Failed to fetch employees:", err);
      });
  }, [
    TOKEN,
    COMPANY_ID,
    filter,
    activeEmployeeId,
    isEmployeeBulkUploadModalOpen,
  ]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await getDepartments(TOKEN, COMPANY_ID);
        return setDepartments(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchDepartments();
  }, [TOKEN, COMPANY_ID]);

  useEffect(() => {
    const fetchCompanyPlan = async () => {
      try {
        const company = await getCompanyDetails(TOKEN, COMPANY_ID);
        const payrollPlan = await getPlanService(TOKEN, company?.data?.planId);
        return setPayrollPlan(payrollPlan?.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchCompanyPlan();
  }, [TOKEN, COMPANY_ID]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1);
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDropDownClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveEmployeeId(null);
    }
  };

  useEffect(() => {
    if (activeEmployeeId !== null) {
      document.addEventListener("mousedown", handleDropDownClickOutside);
    } else {
      document.removeEventListener("mousedown", handleDropDownClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleDropDownClickOutside);
    };
  }, [activeEmployeeId]);

  const handleAddEmployeeButtonClick = (e) => {
    e.stopPropagation();
    return navigateToAddNewEmployee(e);
  };

  const handleBulkImportButtonClick = (e) => {
    e.stopPropagation();
    setBulkImportAnchorElement(e.currentTarget);
    setIsAddEmployeeModalOpen(true);
  };

  const navigateToAddNewEmployee = (e) => {
    e.preventDefault();
    return navigate("/addnewemployee");
  };

  const handleRowItemClick = (e, employeeId) => {
    e.stopPropagation();
    return setActiveEmployeeId(employeeId);
  };

  const deleteEmployee = async (employeeId) => {
    try {
      const response = await deleteEmployeeService(
        TOKEN,
        COMPANY_ID,
        employeeId,
      );
      if (response.status === "Success") {
        setFlag("delete");
        return setIsSuccessModalOpen(true);
      } else {
        setError("Delete department operation failed. Please try again.");
        return console.error(
          "Delete department operation failed. Please try again.",
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowItemActionClick = async (e, employeeId, action) => {
    e.stopPropagation();
    if (!activeEmployeeId) return;
    switch (action) {
      case "edit":
        navigate(`/employees/${employeeId}`);
        break;
      case "delete":
        await deleteEmployee(employeeId);
        break;
      default:
        return;
    }
    return setActiveEmployeeId(null);
  };

  const handleDownloadBulkEmployeeTemplate = async () => {
    try {
      const blob = await getBulkEmployeeUploadTemplate(TOKEN);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "employee-upload-template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download template:", error);
    }
  };

  const handleUploadActionItemClick = async (e, action) => {
    e.stopPropagation();
    switch (action) {
      case "bulk-upload":
        setIsEmployeeBulkUploadModalOpen(true);
        break;
      case "download-template":
        await handleDownloadBulkEmployeeTemplate();
        break;
      default:
        return;
    }
    setIsAddEmployeeModalOpen(false);
  };

  const formatCurrency = (value) => {
    if (!Number.isFinite(value)) {
      return "0.00";
    }
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getInitials = (fullName) => {
    if (!fullName) {
      return "--";
    }
    const values = fullName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((item) => item[0]?.toUpperCase());
    return values.join("");
  };

  const getDepartmentBadgeStyle = (department) => {
    const colorMap = {
      engineering: { backgroundColor: "#DCFCE7", color: "#047857" },
      operations: { backgroundColor: "#F3F4F6", color: "#4B5563" },
      marketing: { backgroundColor: "#DBEAFE", color: "#1D4ED8" },
      finance: { backgroundColor: "#FEF3C7", color: "#B45309" },
      hr: { backgroundColor: "#FCE7F3", color: "#BE185D" },
    };
    return (
      colorMap[department?.toLowerCase()] || {
        backgroundColor: "#E2E8F0",
        color: "#334155",
      }
    );
  };

  const filteredEmployees = useMemo(() => {
    const employeeTerm = filter.employeeName.trim().toLowerCase();
    if (!employeeTerm) {
      return employees;
    }
    return employees.filter((employee) => {
      const fullName = employee?.fullName?.toLowerCase() || "";
      return fullName.includes(employeeTerm);
    });
  }, [employees, filter.employeeName]);

  const totalEmployees = filteredEmployees.length;
  const totalPages = Math.max(1, Math.ceil(totalEmployees / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredEmployees.slice(start, end);
  }, [filteredEmployees, currentPage]);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 1, currentPage, currentPage + 1];
  }, [currentPage, totalPages]);

  const rangeStart =
    totalEmployees === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalEmployees);

  const employeeCountText = totalEmployees === 1 ? "employee" : "employees";

  return (
    <Layout
      id={"employees"}
      title={"Employees"}
      location={"employees"}
      callToAction={"Add Employee"}
      handleCallToActionClick={handleAddEmployeeButtonClick}
      bulkCallToAction={"Bulk Import"}
      handleBulkCallToActionClick={handleBulkImportButtonClick}
    >
      <EmployeesWrapper>
        <SuccessModal
          open={isSuccessModalOpen}
          handleClickOutside={handlePersistModal}
          className={"delete-employee-success-modal"}
          title={"Success"}
          message={
            flag === "manage plan"
              ? "Plan has been successfully updated"
              : `Employee has been successfully ${flag === "delete" ? "deleted" : "added"}`
          }
          callToAction={"Close"}
          handleCallToActionClick={handleCloseSuccessModal}
        />
        <AddEmployeeModal
          handleActionItemClick={handleUploadActionItemClick}
          anchorElement={bulkImportAnchorElement}
        />
        <EmployeeBulkUploadModal
          width={"40%"}
          height={"auto"}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />
        <EditPlanModal
          width={"35%"}
          height={"223px"}
          currentPlanId={payrollPlan?.title}
          setFlag={setFlag}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />

        <FiltersCard aria-label="Advanced filters">
          <p className="filter-title">
            <FontAwesomeIcon icon={faFilter} />
            Advanced Filters
          </p>
          <div className="filters-grid">
            <BaseFieldSet>
              <Label>Employee Name</Label>
              <BaseInput
                type="text"
                name="employeeName"
                placeholder="Search by name..."
                value={filter.employeeName}
                onChange={handleChange}
              />
            </BaseFieldSet>
            <BaseFieldSet>
              <Label>Department</Label>
              <BaseSelect
                name="departmentId"
                onChange={handleChange}
                value={filter.departmentId}
              >
                <option value="">All Departments</option>
                {departments.map((department, index) => (
                  <option key={index} value={department.departmentId}>
                    {department.name.replace(/\b\w/g, (char) =>
                      char.toUpperCase(),
                    )}
                  </option>
                ))}
              </BaseSelect>
            </BaseFieldSet>
            <BaseFieldSet>
              <Label>Job Title</Label>
              <BaseInput
                type="text"
                name="jobTitle"
                placeholder="e.g. Lead Developer"
                value={filter.jobTitle}
                onChange={handleChange}
              />
            </BaseFieldSet>
          </div>
        </FiltersCard>

        <EmployeeTableCard>
          <table className="employee-grid">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Salary (Annual)</th>
                <th>Hire Date</th>
                <th>Role</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEmployees?.map((rowItem, index) => {
                const departmentName =
                  rowItem?.jobInformation?.department || "Not assigned";
                return (
                  <tr key={rowItem?.employeeId || index}>
                    <td>
                      <div className="employee-cell">
                        <span className="avatar">
                          {getInitials(rowItem?.fullName)}
                        </span>
                        <div className="employee-meta">
                          <span className="employee-name">
                            {rowItem?.fullName || "Unnamed Employee"}
                          </span>
                          <span className="employee-email">
                            {rowItem?.email || rowItem?.workEmail || "-"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className="department-tag"
                        style={getDepartmentBadgeStyle(departmentName)}
                      >
                        {departmentName}
                      </span>
                    </td>
                    <td className="salary">
                      {formatCurrency(
                        rowItem?.payrollSetupInformation?.annualGrossPay,
                      )}
                    </td>
                    <td>{formatDate(rowItem?.jobInformation?.dateHired)}</td>
                    <td className="role">
                      {rowItem?.jobInformation?.jobPosition || "-"}
                    </td>
                    <td
                      className="action-cell"
                      onClick={(e) =>
                        handleRowItemClick(e, rowItem?.employeeId)
                      }
                    >
                      <span className="action-trigger">
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </span>
                      {activeEmployeeId === rowItem?.employeeId && (
                        <ul ref={dropdownRef} className="action-dropdown">
                          <li
                            onClick={(e) =>
                              handleRowItemActionClick(
                                e,
                                rowItem?.employeeId,
                                "edit",
                              )
                            }
                          >
                            Edit Employee
                          </li>
                          <li
                            onClick={(e) =>
                              handleRowItemActionClick(
                                e,
                                rowItem?.employeeId,
                                "delete",
                              )
                            }
                          >
                            Delete Employee
                          </li>
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="table-footer">
            <span>
              Showing {rangeStart}-{rangeEnd} of {totalEmployees}{" "}
              {employeeCountText}
            </span>
            <div className="pagination" aria-hidden="true">
              <button
                type="button"
                className="page-chip"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                &lt;
              </button>
              {visiblePageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={`page-chip ${currentPage === pageNumber ? "active" : ""}`}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                className="page-chip"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                &gt;
              </button>
            </div>
          </div>
        </EmployeeTableCard>

        <div>{error && <P className="error-text">{error}</P>}</div>
      </EmployeesWrapper>
    </Layout>
  );
};
